import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './AdminKiosks.css';

export default function AdminKiosks({ user }) {
  const [kiosks, setKiosks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [form, setForm] = useState({ booth_id: '', label: '', allowed_ips: '' });
  const [deviceId, setDeviceId] = useState('');
  const [deviceType, setDeviceType] = useState('kiosk');
  const [selectedKiosk, setSelectedKiosk] = useState('');
  const [selectedDesk, setSelectedDesk] = useState('');
  const [error, setError] = useState(null);
  const [desks, setDesks] = useState([]);

  const fetchKiosks = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/admin/kiosks');
      setKiosks(res.data || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKiosks();
    fetchDesks();
  }, []);

  const fetchDesks = async () => {
    try {
      const res = await axios.get('/api/admin/desks');
      setDesks(res.data || []);
    } catch (err) {
      console.error('Failed to fetch desks:', err.message);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError(null);
    try {
      await axios.post('/api/admin/kiosks', form);
      setForm({ booth_id: '', label: '', allowed_ips: '' });
      fetchKiosks();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleEnrollDevice = async (e) => {
    e.preventDefault();
    setEnrolling(true);
    setError(null);
    try {
      const payload = {
        device_id: deviceId,
        device_type: deviceType
      };
      if (deviceType === 'kiosk' || deviceType === 'room_display') {
        payload.kiosk_id = selectedKiosk;
      } else if (deviceType === 'desk_display') {
        payload.desk_id = selectedDesk;
      }
      await axios.post('/api/admin/devices/enroll', payload);
      setDeviceId('');
      setDeviceType('kiosk');
      setSelectedKiosk('');
      setSelectedDesk('');
      fetchKiosks();
      alert('Device enrolled successfully!');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setEnrolling(false);
    }
  };

  const handleRegenerate = async (id) => {
    if (!window.confirm('Regenerate pairing code and token for this kiosk?')) return;
    try {
      await axios.put(`/api/admin/kiosks/${id}/regenerate`);
      fetchKiosks();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this kiosk and its devices/heartbeats?')) return;
    try {
      await axios.delete(`/api/admin/kiosks/${id}`);
      fetchKiosks();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  return (
    <div className="admin-kiosks">
      <h2>Kiosk Management</h2>
      {error && <div className="error">{error}</div>}

      <form className="kiosk-form" onSubmit={handleCreate}>
        <input placeholder="Booth ID (e.g. Booth-1)" value={form.booth_id} onChange={e => setForm({ ...form, booth_id: e.target.value })} required />
        <input placeholder="Label (Booth 1)" value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} />
        <input placeholder="Allowed IPs (comma)" value={form.allowed_ips} onChange={e => setForm({ ...form, allowed_ips: e.target.value })} />
        <button type="submit" disabled={creating}>Create Kiosk</button>
      </form>

      {loading ? (
        <div>Loading kiosks...</div>
      ) : (
        <table className="kiosk-table">
          <thead>
            <tr>
              <th>Booth ID</th>
              <th>Label</th>
              <th>Pairing Code</th>
              <th>Token</th>
              <th>Last Heartbeat</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {kiosks.map(k => (
              <tr key={k.id}>
                <td>{k.booth_id}</td>
                <td>{k.label}</td>
                <td><code>{k.pairing_code}</code></td>
                <td><code style={{ maxWidth: 'none', display: 'inline-block', overflow: 'auto', whiteSpace: 'nowrap' }}>{k.token}</code></td>
                <td>{k.last_heartbeat || '-'}</td>
                <td>
                  <button onClick={() => handleRegenerate(k.id)}>Regenerate</button>
                  <button className="danger" onClick={() => handleDelete(k.id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="device-enrollment-section">
        <h3>Enroll Device</h3>
        <form className="device-enrollment-form" onSubmit={handleEnrollDevice}>
          <input placeholder="Device ID (e.g. esp-4CC382D32198)" value={deviceId} onChange={e => setDeviceId(e.target.value)} required />

          <select value={deviceType} onChange={e => setDeviceType(e.target.value)} required>
            <option value="kiosk">Booth Kiosk (Tablet)</option>
            <option value="room_display">Room Display (Meeting Room)</option>
            <option value="desk_display">Desk Display Monitor (ESP32)</option>
          </select>

          {(deviceType === 'kiosk' || deviceType === 'room_display') ? (
            <select value={selectedKiosk} onChange={e => setSelectedKiosk(e.target.value)} required>
              <option value="">Select {deviceType === 'room_display' ? 'Meeting Room' : 'Kiosk'}</option>
              {kiosks.map(k => (
                <option key={k.id} value={k.id}>{k.label || k.booth_id} ({k.booth_id})</option>
              ))}
            </select>
          ) : (
            <select value={selectedDesk} onChange={e => setSelectedDesk(e.target.value)} required>
              <option value="">Select Desk</option>
              {desks.map(d => (
                <option key={d.id} value={d.id}>{d.id} ({d.zone})</option>
              ))}
            </select>
          )}

          <button type="submit" disabled={enrolling}>Enroll Device</button>
        </form>
      </div>
    </div>
  );
}
